export type PracticePurpose = "settle" | "focus" | "prepare";
export interface PracticeProtocol {
  id: string;
  version: string;
  title: string;
  purpose: PracticePurpose;
  instructions: string[];
  evidence: string;
  limits: string;
  source: string;
  safety: string;
}
export const PROTOCOLS: PracticeProtocol[] = [
  {
    id: "comfortable-breathing",
    version: "2026-10-06.1",
    title: "Comfortable breathing",
    purpose: "settle",
    instructions: [
      "Choose a comfortable, supported position. Keep your eyes open if you prefer.",
      "Follow an easy breath in for about 4 seconds, then out for about 6. No holds.",
      "Use a smaller breath or your natural pace whenever needed.",
    ],
    evidence:
      "Adult trials suggest brief breathing practice can help some people feel calmer. An active-control trial found no added benefit from a specific slow rate.",
    limits:
      "This short app session is an adaptation, not the studied daily program. Effects vary; benefits for adolescents and this app have not been established.",
    source: "https://www.nature.com/articles/s41598-023-49279-8",
    safety:
      "Stop pacing if you feel dizzy, short of breath, panicky, or uncomfortable. Return to natural breathing or choose grounding. Never practice while driving or in water. Seek urgent care for severe or unfamiliar symptoms.",
  },
  {
    id: "orient-to-room",
    version: "2026-10-06.1",
    title: "Notice your surroundings",
    purpose: "settle",
    instructions: [
      "Keep breathing naturally. Notice a comfortable point of contact, such as your feet or chair.",
      "Notice up to three details around you using any comfortable sense. You might notice an object, a sound, or a surface.",
      "Notice a sound or another point of contact. Choose one small next action. You can use any available sense.",
    ],
    evidence:
      "Grounding is included in the WHO stress-management guide, informed by evidence and field testing.",
    limits:
      "These original prompts adapt the general grounding approach. This exact activity has not been evaluated for effectiveness.",
    source: "https://www.who.int/publications/i/item/9789240003927",
    safety:
      "Skip any sensation that feels uncomfortable. Keep eyes open, change position, or stop. You do not have to scan your body.",
  },
  {
    id: "one-next-step",
    version: "2026-10-05.1",
    title: "Choose one manageable step",
    purpose: "focus",
    instructions: [
      "Name one task you want to start. You can keep it in your head.",
      "Pick a step small enough to try in the next two minutes. Put aside one distraction if possible.",
      "Make a plan: “When I notice ___, I will try ___.” Starting, asking for help, or taking a needed rest are valid steps.",
    ],
    evidence:
      "Planning, monitoring, and reflection are established self-regulated learning approaches. The research review explains the evidence behind these components.",
    limits:
      "A planning prompt is not a treatment for attention difficulties and cannot guarantee focus or academic gains.",
    source: "https://doi.org/10.3389/fpsyg.2017.00422",
    safety:
      "Choose a realistic step. Rest, access needs, and asking for support count; do not force yourself through pain or distress.",
  },
  {
    id: "challenge-plan",
    version: "2026-10-05.1",
    title: "Rehearse a helpful response",
    purpose: "prepare",
    instructions: [
      "Think of a manageable upcoming challenge, or use a fictional example.",
      "Name what you can influence: a strategy, an ask for help, or a first step.",
      "Rehearse an “if–then” plan. Difficulty can be a cue to adjust your strategy; unsafe situations call for safety and support.",
    ],
    evidence:
      "Research on growth mindset and stress reappraisal supports contextual, structured interventions. The original study involved adolescents facing academic stress.",
    limits:
      "This brief rehearsal differs from the tested intervention. Do not assume all stress is helpful or that effort alone removes barriers.",
    source: "https://www.nature.com/articles/s41586-022-04907-7",
    safety:
      "Use a low-stakes or fictional situation. You may stop or switch activities. Abuse, danger, and severe distress need support, not reframing.",
  },
];
export interface PracticeReceipt {
  id: string;
  protocolId: string;
  protocolVersion: string;
  purpose: PracticePurpose;
  completedAt: string;
  activeSeconds: number;
  plannedSeconds: number;
  status: "completed" | "stopped";
  helpfulness?: "helped" | "same" | "worse";
  plan?: string;
  followUps: Array<{
    day: 3 | 7;
    dueAt: string;
    answeredAt?: string;
    used?: boolean;
  }>;
}
export const SCENARIOS = [
  {
    prompt: "You get stuck on a difficult task. What is a useful next step?",
    options: [
      "Repeat the same approach until it works",
      "Try a smaller step or a different strategy, and ask for help if needed",
      "Treat difficulty as proof you cannot learn",
    ],
    answer: 1,
    feedback:
      "A strategy change and appropriate support are practical responses to difficulty.",
  },
  {
    prompt: "Paced breathing makes you light-headed. What should you do?",
    options: [
      "Breathe more deeply and finish for the points",
      "Stop pacing, breathe naturally, and choose a comfortable alternative",
      "Hold your breath to reset",
    ],
    answer: 1,
    feedback:
      "Comfort and safety come first. You can stop; grounding is a breathing-free alternative.",
  },
  {
    prompt: "A situation is unsafe. Which response fits?",
    options: [
      "Reframe it as an opportunity and stay",
      "Ignore your feelings",
      "Seek safety and appropriate help",
    ],
    answer: 2,
    feedback:
      "Reframing is for manageable challenges. Danger calls for safety and support.",
  },
];
